import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  detectLegacyJumpLogic,
  JumpLogicEvaluator,
  NON_ANSWERABLE_ELEMENT_TYPES,
  VisibilityEvaluator,
  type FormQuestion,
} from "@kushya/shared";

import { trackFormEvent } from "@/api/analytics";
import {
  completeResponseSession,
  createResponseSession,
  getSharedFormState,
  markResponseSessionLeave,
  sendResponseSessionLeaveBeacon,
  submitResponse,
  updateSharedFormState,
  updateResponseSessionProgress,
  type ValidationError as SubmissionValidationError,
} from "@/api/responses";
import { createUploadSession } from "@/lib/api/upload";
import { validateAnswersClientSide } from "@/utils/validation";
import { useFormDraftPersistence } from "@/hooks/useFormDraftPersistence";
import type { StoredFormDraft } from "@/lib/form-draft-storage";
import {
  isAnswerProvided,
  shouldRequireAnswer,
} from "./utils/answerCompleteness";
import { useKeyboardShortcuts } from "./view-model/useKeyboardShortcuts";
import {
  getDraftIdentity,
  getHasDraftProgress,
  getQuestionRefSet,
  filterAnswersToKnownQuestions,
  sanitizePageHistory,
  sanitizeQuestionHistory,
  sanitizeQuestionPath,
  buildDraftSnapshot,
} from "./view-model/draft";
import {
  getPageIds,
  getPageQuestions as resolvePageQuestions,
  getStepIndexForPageIndex,
  sortPages,
} from "./view-model/pages";
import {
  getProgressMode,
  getProgressPercentage,
  getShowProgress,
} from "./view-model/progress";
import {
  handleBack,
  handleNext,
  handleSubmit,
} from "./view-model/flowHandlers";
import type {
  AnswerRecord,
  FormViewModel,
  UseFormViewModelParams,
} from "./view-model/types";

export type { FormViewModel, UseFormViewModelParams } from "./view-model/types";

type RuntimeResponseSession = {
  responseId: string;
  sessionToken: string;
};

const nonAnswerableQuestionTypes = new Set<string>(NON_ANSWERABLE_ELEMENT_TYPES);
const isTrackableQuestion = (question: FormQuestion) =>
  !nonAnswerableQuestionTypes.has(question.type);

export function useFormViewModel({
  form,
  projectId,
  formId,
  isPreview = false,
}: UseFormViewModelParams): FormViewModel {
  const questions = form?.questions ?? [];
  const totalQuestions = questions.length;
  const trackableQuestions = useMemo(
    () => questions.filter(isTrackableQuestion),
    [questions],
  );
  const trackableQuestionRefSet = useMemo(
    () => new Set(trackableQuestions.map((question) => question.ref)),
    [trackableQuestions],
  );
  const totalTrackableQuestions = trackableQuestions.length;
  const formIdentity = form
    ? `${form.projectId}:${form.formId}:${form.version}`
    : undefined;
  const isSharedMode = !isPreview && form?.responseMode === "shared";
  const shouldUseLocalDrafts = !isPreview && !isSharedMode;
  const progressMode = getProgressMode(form);

  const introductionCopy = form?.ui.copy?.introduction?.trim();
  const hasIntro = Boolean(introductionCopy);

  const draftIdentity = useMemo(() => getDraftIdentity(form), [form]);

  const {
    draft: storedDraft,
    isRestoringDraft,
    saveDraft,
    clearDraft,
  } = useFormDraftPersistence(draftIdentity, { enabled: shouldUseLocalDrafts });

  const [pendingDraft, setPendingDraft] = useState<StoredFormDraft | null>(
    null,
  );
  const activePendingDraft = shouldUseLocalDrafts ? pendingDraft : null;
  const hasInitializedForFormRef = useRef<string | null>(null);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<AnswerRecord>({});
  const answersRef = useRef<AnswerRecord>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(() => new Date());
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    SubmissionValidationError[]
  >([]);
  const [uploadSessionToken, setUploadSessionToken] = useState<string | null>(
    null,
  );
  const [touchedQuestions, setTouchedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(
    new Map(),
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [responseSession, setResponseSession] = useState<RuntimeResponseSession | null>(null);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const [questionHistory, setQuestionHistory] = useState<number[]>([]);
  const [visitedQuestionRefs, setVisitedQuestionRefs] = useState<Set<string>>(
    () => new Set(),
  );
  const [questionPath, setQuestionPath] = useState<string[]>([]);
  const [pageHistory, setPageHistory] = useState<number[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const isCreatingSessionRef = useRef(false);
  const sessionCreationPromiseRef = useRef<Promise<RuntimeResponseSession> | null>(
    null,
  );
  const lastProgressPayloadRef = useRef<string | null>(null);
  const dirtySharedRefsRef = useRef<Set<string>>(new Set());
  const savingSharedRefsRef = useRef<Set<string>>(new Set());
  const [isRestoringSharedState, setIsRestoringSharedState] = useState(false);

  const orderedPages = useMemo(
    () => sortPages(form?.pages ?? []),
    [form?.pages],
  );
  const hasPages = orderedPages.length > 0;
  const unassignedQuestions = useMemo(
    () => questions.filter((question) => !question.pageId),
    [questions],
  );
  const pageIds = useMemo(() => {
    if (!hasPages) {
      return [];
    }
    return getPageIds(orderedPages, unassignedQuestions.length > 0);
  }, [hasPages, orderedPages, unassignedQuestions.length]);
  const totalPages = pageIds.length;

  const shouldInitUploadSession = useMemo(() => {
    if (!form || isPreview) {
      return false;
    }

    if (form.ui?.layout?.variant === "conversational") {
      return false;
    }

    return questions.some(
      (question) =>
        question.type === "file_upload" ||
        question.type === "signature" ||
        (question.type === "table" &&
          question.columns.some((column) => column.type === "signature")),
    );
  }, [form, questions, isPreview]);

  const requestUploadSessionToken = useCallback(async (): Promise<string | null> => {
    if (!form) {
      return null;
    }

    const sessionToken = await createUploadSession({
      projectId: form.projectId,
      formId: form.formId,
      version: form.version,
    });

    setUploadSessionToken(sessionToken);
    return sessionToken;
  }, [form]);

  const ensureUploadSessionToken = useCallback(async (): Promise<string | null> => {
    if (uploadSessionToken) {
      return uploadSessionToken;
    }

    try {
      return await requestUploadSessionToken();
    } catch {
      return null;
    }
  }, [requestUploadSessionToken, uploadSessionToken]);

  useEffect(() => {
    if (!form || !formIdentity || !shouldInitUploadSession) {
      setUploadSessionToken(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const sessionToken = await requestUploadSessionToken();

        if (!cancelled && sessionToken) {
          setUploadSessionToken(sessionToken);
        }
      } catch {
        if (!cancelled) {
          setUploadSessionToken(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form, formIdentity, requestUploadSessionToken, shouldInitUploadSession]);

  const getQuestionsForPage = useCallback(
    (pageId: string | null | undefined) => {
      return resolvePageQuestions(questions, unassignedQuestions, pageId);
    },
    [questions, unassignedQuestions],
  );

  // Get all questions for the current page (before visibility filtering)
  const allCurrentPageQuestions = hasPages
    ? getQuestionsForPage(pageIds[currentPageIndex])
    : undefined;

  const shouldShowIntro = hasIntro || Boolean(activePendingDraft);
  const isOnIntro = shouldShowIntro && currentStep === -1;
  const isOnQuestion =
    !hasPages && currentStep >= 0 && currentStep < totalQuestions;
  const currentQuestion =
    !hasPages && isOnQuestion ? questions[currentStep] : undefined;
  const isComplete = hasPages
    ? currentPageIndex >= totalPages
    : totalQuestions === 0
      ? currentStep > -1
      : currentStep >= totalQuestions;
  const isOnPagedFlowStep = hasPages && !isOnIntro && !isComplete;

  const jumpEvaluator = useMemo(
    () => new JumpLogicEvaluator(questions),
    [questions],
  );
  const visibilityEvaluator = useMemo(
    () => new VisibilityEvaluator(questions),
    [questions],
  );
  const legacyJumpIssues = useMemo(
    () => detectLegacyJumpLogic(questions),
    [questions],
  );
  const hasLoggedLegacyJump = useRef(false);

  useEffect(() => {
    if (hasLoggedLegacyJump.current || legacyJumpIssues.length === 0) {
      return;
    }

    hasLoggedLegacyJump.current = true;
    // eslint-disable-next-line no-console
    console.warn(
      "Legacy jump logic detected. Page-level jump logic is now required.",
      legacyJumpIssues,
    );
  }, [legacyJumpIssues]);

  // Filter page questions by visibility based on current answers
  const currentPageQuestions = useMemo(() => {
    if (!allCurrentPageQuestions) {
      return undefined;
    }
    return visibilityEvaluator.filterVisible(allCurrentPageQuestions, answers);
  }, [allCurrentPageQuestions, answers, visibilityEvaluator]);

  const skippedQuestionRefs = useMemo(() => {
    if (trackableQuestions.length === 0) {
      return [] as string[];
    }
    return trackableQuestions
      .filter((question) => !visitedQuestionRefs.has(question.ref))
      .map((question) => question.ref);
  }, [trackableQuestions, visitedQuestionRefs]);

  const reset = useCallback(
    (options?: { preferIntro?: boolean }) => {
      const shouldGoToIntro = options?.preferIntro ?? hasIntro;

      setCurrentStep(shouldGoToIntro ? -1 : 0);
      answersRef.current = {};
      setAnswers({});
      setIsSubmitting(false);
      setSubmissionError(null);
      setValidationErrors([]);
      setTouchedQuestions(new Set());
      setFieldErrors(new Map());
      setIsSubmitted(false);
      setResponseId(null);
      setResponseSession(null);
      setStartTime(new Date());
      setHasTrackedStart(false);
      setQuestionHistory([]);
      setVisitedQuestionRefs(new Set<string>());
      setQuestionPath([]);
      setPageHistory([]);
      setCurrentPageIndex(0);
      isCreatingSessionRef.current = false;
      sessionCreationPromiseRef.current = null;
      lastProgressPayloadRef.current = null;
      dirtySharedRefsRef.current.clear();
      savingSharedRefsRef.current.clear();
    },
    [hasIntro],
  );

  useEffect(() => {
    if (!shouldUseLocalDrafts) {
      setPendingDraft(null);
    }
  }, [shouldUseLocalDrafts, formIdentity]);

  useEffect(() => {
    if (!form || !formIdentity) {
      return;
    }

    if (isRestoringDraft) {
      return;
    }

    if (hasInitializedForFormRef.current === formIdentity) {
      return;
    }

    hasInitializedForFormRef.current = formIdentity;
    const nextStoredDraft = shouldUseLocalDrafts ? storedDraft : null;
    setPendingDraft(nextStoredDraft);

    reset({ preferIntro: hasIntro || Boolean(nextStoredDraft) });

    // Skip analytics in preview mode
    if (projectId && formId && !isPreview) {
      void trackFormEvent(projectId, formId, "form_viewed");
    }
  }, [
    form,
    formIdentity,
    formId,
    hasIntro,
    isPreview,
    isRestoringDraft,
    projectId,
    reset,
    shouldUseLocalDrafts,
    storedDraft,
  ]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (!isSharedMode || !form || !formIdentity) {
      setIsRestoringSharedState(false);
      return;
    }

    let cancelled = false;
    setIsRestoringSharedState(true);

    void getSharedFormState(form.projectId, form.formId)
      .then((state) => {
        if (cancelled) {
          return;
        }
        dirtySharedRefsRef.current.clear();
        savingSharedRefsRef.current.clear();
        answersRef.current = state.answers;
        setAnswers(state.answers);
        setResponseId(state.responseId);
        setSubmissionError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setSubmissionError("Shared form state could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsRestoringSharedState(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form, formIdentity, isSharedMode]);

  const currentStepRefsJson = useMemo(() => {
    if (hasPages) {
      return JSON.stringify(
        (currentPageQuestions ?? [])
          .filter(
            (question) =>
              isTrackableQuestion(question) &&
              isAnswerProvided(question, answers[question.ref]),
          )
          .map((q) => q.ref),
      );
    }

    return JSON.stringify(
      currentQuestion && isTrackableQuestion(currentQuestion)
        ? [currentQuestion.ref]
        : [],
    );
  }, [answers, currentPageQuestions, currentQuestion, hasPages]);

  useEffect(() => {
    const refs = JSON.parse(currentStepRefsJson) as string[];

    if (refs.length === 0) {
      return;
    }

    setVisitedQuestionRefs((prev) => {
      let changed = false;
      const next = new Set(prev);
      refs.forEach((ref) => {
        if (!next.has(ref)) {
          next.add(ref);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setQuestionPath((prev) => {
      let changed = false;
      const next = [...prev];
      refs.forEach((ref) => {
        if (next[next.length - 1] !== ref) {
          next.push(ref);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentStepRefsJson]);

  useEffect(() => {
    if (!form || !hasPages || isOnIntro || isComplete) {
      return;
    }

    const visibleOnCurrentPage = currentPageQuestions ?? [];
    if (visibleOnCurrentPage.length > 0) {
      return;
    }

    let resolvedPageIndex: number | null = null;

    for (
      let pageIndex = Math.min(currentPageIndex + 1, totalPages);
      pageIndex < totalPages;
      pageIndex += 1
    ) {
      const pageQuestions = getQuestionsForPage(pageIds[pageIndex]);
      const visibleQuestions = visibilityEvaluator.filterVisible(
        pageQuestions,
        answersRef.current,
      );
      if (visibleQuestions.length > 0) {
        resolvedPageIndex = pageIndex;
        break;
      }
    }

    if (resolvedPageIndex === null) {
      for (
        let pageIndex = currentPageIndex - 1;
        pageIndex >= 0;
        pageIndex -= 1
      ) {
        const pageQuestions = getQuestionsForPage(pageIds[pageIndex]);
        const visibleQuestions = visibilityEvaluator.filterVisible(
          pageQuestions,
          answersRef.current,
        );
        if (visibleQuestions.length > 0) {
          resolvedPageIndex = pageIndex;
          break;
        }
      }
    }

    if (resolvedPageIndex !== null && resolvedPageIndex !== currentPageIndex) {
      setCurrentPageIndex(resolvedPageIndex);
      setCurrentStep(
        getStepIndexForPageIndex(
          resolvedPageIndex,
          pageIds,
          getQuestionsForPage,
        ),
      );
    }
  }, [
    currentPageIndex,
    currentPageQuestions,
    form,
    getQuestionsForPage,
    hasPages,
    isComplete,
    isOnIntro,
    pageIds,
    totalPages,
    visibilityEvaluator,
  ]);

  useEffect(() => {
    if (!form || hasPages || isOnIntro || isComplete) {
      return;
    }

    if (!currentQuestion) {
      return;
    }

    if (visibilityEvaluator.isVisible(currentQuestion, answersRef.current)) {
      return;
    }

    let resolvedStep: number | null = null;

    for (let index = currentStep + 1; index < totalQuestions; index += 1) {
      const question = questions[index];
      if (
        question &&
        visibilityEvaluator.isVisible(question, answersRef.current)
      ) {
        resolvedStep = index;
        break;
      }
    }

    if (resolvedStep === null) {
      for (let index = currentStep - 1; index >= 0; index -= 1) {
        const question = questions[index];
        if (
          question &&
          visibilityEvaluator.isVisible(question, answersRef.current)
        ) {
          resolvedStep = index;
          break;
        }
      }
    }

    if (resolvedStep !== null && resolvedStep !== currentStep) {
      setCurrentStep(resolvedStep);
    }
  }, [
    currentQuestion,
    currentStep,
    form,
    hasPages,
    isComplete,
    isOnIntro,
    questions,
    totalQuestions,
    visibilityEvaluator,
  ]);

  useEffect(() => {
    // Skip analytics in preview mode
    if (!projectId || !formId || isPreview) {
      return;
    }
    if ((isOnQuestion || isOnPagedFlowStep) && !hasTrackedStart) {
      void trackFormEvent(projectId, formId, "form_started");
      setHasTrackedStart(true);
    }
  }, [
    formId,
    hasTrackedStart,
    isOnPagedFlowStep,
    isOnQuestion,
    isPreview,
    projectId,
  ]);

  const onResumeDraft = useCallback(() => {
    if (!form || !activePendingDraft) {
      return;
    }

    const questionRefs = getQuestionRefSet(questions);
    const nextAnswers = filterAnswersToKnownQuestions(
      activePendingDraft.answers,
      questionRefs,
    );
    const safeQuestionPath = sanitizeQuestionPath(
      activePendingDraft.questionPath,
      questionRefs,
    ).filter((ref) => trackableQuestionRefSet.has(ref));

    const startedAt = new Date(activePendingDraft.startedAt);

    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    setTouchedQuestions(new Set());
    setFieldErrors(new Map());
    setValidationErrors([]);
    setSubmissionError(null);
    setIsSubmitted(false);
    setResponseId(null);
    setQuestionHistory(
      sanitizeQuestionHistory(activePendingDraft.questionHistory, totalQuestions),
    );
    setPageHistory(sanitizePageHistory(activePendingDraft.pageHistory, totalPages));
    setQuestionPath(safeQuestionPath);
    setVisitedQuestionRefs(new Set<string>(safeQuestionPath));
    setStartTime(Number.isNaN(startedAt.getTime()) ? new Date() : startedAt);

    const hasAnyProgress =
      Object.keys(nextAnswers).length > 0 ||
      safeQuestionPath.length > 0 ||
      (activePendingDraft.currentPageIndex ?? 0) > 0 ||
      (activePendingDraft.currentStep ?? 0) > 0;
    if (hasAnyProgress) {
      setHasTrackedStart(true);
    }

    if (hasPages) {
      const restoredPageIndex = Math.max(
        0,
        Math.min(activePendingDraft.currentPageIndex ?? 0, Math.max(0, totalPages)),
      );
      let resolvedPageIndex: number | null = null;

      for (
        let pageIndex = restoredPageIndex;
        pageIndex < totalPages;
        pageIndex += 1
      ) {
        const pageQuestions = getQuestionsForPage(pageIds[pageIndex]);
        const visibleQuestions = visibilityEvaluator.filterVisible(
          pageQuestions,
          nextAnswers,
        );
        if (visibleQuestions.length > 0) {
          resolvedPageIndex = pageIndex;
          break;
        }
      }

      if (resolvedPageIndex === null) {
        for (
          let pageIndex = Math.min(restoredPageIndex - 1, totalPages - 1);
          pageIndex >= 0;
          pageIndex -= 1
        ) {
          const pageQuestions = getQuestionsForPage(pageIds[pageIndex]);
          const visibleQuestions = visibilityEvaluator.filterVisible(
            pageQuestions,
            nextAnswers,
          );
          if (visibleQuestions.length > 0) {
            resolvedPageIndex = pageIndex;
            break;
          }
        }
      }

      const safePageIndex = resolvedPageIndex ?? 0;

      setCurrentPageIndex(safePageIndex);
      setCurrentStep(
        getStepIndexForPageIndex(safePageIndex, pageIds, getQuestionsForPage),
      );
    } else {
      const restoredStep = Math.max(0, activePendingDraft.currentStep ?? 0);
      const safeStart = Math.min(restoredStep, Math.max(0, totalQuestions));
      let resolvedStep: number | null = null;

      for (let index = safeStart; index < totalQuestions; index += 1) {
        const question = questions[index];
        if (question && visibilityEvaluator.isVisible(question, nextAnswers)) {
          resolvedStep = index;
          break;
        }
      }

      if (resolvedStep === null) {
        for (
          let index = Math.min(safeStart - 1, totalQuestions - 1);
          index >= 0;
          index -= 1
        ) {
          const question = questions[index];
          if (
            question &&
            visibilityEvaluator.isVisible(question, nextAnswers)
          ) {
            resolvedStep = index;
            break;
          }
        }
      }

      setCurrentStep(resolvedStep ?? 0);
    }

    setPendingDraft(null);
  }, [
    form,
    getQuestionsForPage,
    hasPages,
    pageIds,
    activePendingDraft,
    questions,
    trackableQuestionRefSet,
    totalPages,
    totalQuestions,
    visibilityEvaluator,
  ]);

  const onDiscardDraft = useCallback(() => {
    clearDraft();
    setPendingDraft(null);
    reset({ preferIntro: hasIntro });
  }, [clearDraft, hasIntro, reset]);

  const questionsBeforeCurrentPage = useMemo(() => {
    if (!hasPages) {
      return 0;
    }
    return getStepIndexForPageIndex(
      currentPageIndex,
      pageIds,
      getQuestionsForPage,
    );
  }, [currentPageIndex, getQuestionsForPage, hasPages, pageIds]);

  const progressPercentage = getProgressPercentage({
    progressMode,
    hasPages,
    hasIntro,
    currentPageIndex,
    currentStep,
    totalPages,
    totalQuestions: totalTrackableQuestions,
    questionsBeforeCurrentPage,
    visitedQuestionCount: visitedQuestionRefs.size,
  });

  const lastQuestionRef = questionPath[questionPath.length - 1];
  const currentLandingQuestionRef = hasPages
    ? (currentPageQuestions ?? []).find(isTrackableQuestion)?.ref
    : currentQuestion && isTrackableQuestion(currentQuestion)
      ? currentQuestion.ref
      : undefined;
  const effectiveLastQuestionRef = lastQuestionRef ?? currentLandingQuestionRef;
  const currentPageId =
    hasPages && !isOnIntro && !isComplete ? pageIds[currentPageIndex] : undefined;

  const createAndStoreSession = useCallback(
    async (input: {
      startedAt: Date;
      answers: AnswerRecord;
      skippedQuestionRefs: string[];
      questionPath: string[];
    }): Promise<RuntimeResponseSession> => {
      if (!form) {
        throw new Error("Form is required");
      }

      const created = await createResponseSession({
        projectId: form.projectId,
        formId: form.formId,
        versionNumber: form.version,
        startedAt: input.startedAt,
        answers: input.answers,
        skippedQuestionRefs: input.skippedQuestionRefs,
        questionPath: input.questionPath,
        progressPercentage,
        lastQuestionRef: effectiveLastQuestionRef,
        lastPageId: currentPageId,
      });

      const nextSession: RuntimeResponseSession = {
        responseId: created.responseId,
        sessionToken: created.sessionToken,
      };

      setResponseSession(nextSession);
      lastProgressPayloadRef.current = null;
      return nextSession;
    },
    [currentPageId, effectiveLastQuestionRef, form, progressPercentage],
  );

  const showProgress = getShowProgress({
    form,
    hasPages,
    totalPages,
    totalQuestions: totalTrackableQuestions,
  });

  useEffect(() => {
    if (!form || isPreview || isSharedMode || isSubmitted || !hasTrackedStart) {
      return;
    }
    if (responseSession || isCreatingSessionRef.current) {
      return;
    }
    isCreatingSessionRef.current = true;
    sessionCreationPromiseRef.current = createAndStoreSession({
      startedAt: startTime,
      answers,
      skippedQuestionRefs,
      questionPath,
    })
      .catch(() => {
        // Session persistence should not block form completion.
        throw new Error("SESSION_CREATE_FAILED");
      })
      .finally(() => {
        isCreatingSessionRef.current = false;
        sessionCreationPromiseRef.current = null;
      });

    void sessionCreationPromiseRef.current.catch(() => {
      // Ignore session creation failures.
    });
  }, [
    answers,
    createAndStoreSession,
    form,
    hasTrackedStart,
    isPreview,
    isSharedMode,
    isSubmitted,
    questionPath,
    responseSession,
    skippedQuestionRefs,
    startTime,
  ]);

  useEffect(() => {
    if (!form || isPreview || isSharedMode || isSubmitted || isSubmitting || !responseSession) {
      return;
    }

    const payload = {
      answers,
      skippedQuestionRefs,
      questionPath,
      progressPercentage,
      lastQuestionRef: effectiveLastQuestionRef,
      lastPageId: currentPageId,
    };
    const payloadKey = JSON.stringify(payload);

    if (payloadKey === lastProgressPayloadRef.current) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void updateResponseSessionProgress(
        responseSession.responseId,
        responseSession.sessionToken,
        payload,
      )
        .then(() => {
          lastProgressPayloadRef.current = payloadKey;
        })
        .catch(() => {
          // Keep runtime resilient if background analytics write fails.
        });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [
    answers,
    currentPageId,
    effectiveLastQuestionRef,
    form,
    isPreview,
    isSharedMode,
    isSubmitted,
    isSubmitting,
    progressPercentage,
    questionPath,
    responseSession,
    skippedQuestionRefs,
  ]);

  const sendLeaveSignal = useCallback(() => {
    if (!responseSession || isPreview || isSharedMode || isSubmitted) {
      return;
    }

    const payload = {
      answers,
      skippedQuestionRefs,
      questionPath,
      progressPercentage,
      lastQuestionRef: effectiveLastQuestionRef,
      lastPageId: currentPageId,
    };

    const beaconSent = sendResponseSessionLeaveBeacon(
      responseSession.responseId,
      responseSession.sessionToken,
      payload,
    );

    if (!beaconSent) {
      void markResponseSessionLeave(
        responseSession.responseId,
        responseSession.sessionToken,
        payload,
      ).catch(() => {
        // Ignore leave tracking failures during navigation/unload.
      });
    }
  }, [
    answers,
    currentPageId,
    effectiveLastQuestionRef,
    isPreview,
    isSharedMode,
    isSubmitted,
    progressPercentage,
    questionPath,
    responseSession,
    skippedQuestionRefs,
  ]);

  useEffect(() => {
    if (!responseSession || isPreview || isSubmitted) {
      return;
    }

    const handlePageHide = () => {
      sendLeaveSignal();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendLeaveSignal();
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPreview, isSubmitted, responseSession, sendLeaveSignal]);

  const flushSharedAnswers = useCallback(
    async (
      refs?: Iterable<string>,
      options?: { keepalive?: boolean },
    ): Promise<void> => {
      if (!isSharedMode || !form) {
        return;
      }

      const requestedRefs = Array.from(refs ?? dirtySharedRefsRef.current);
      const refsToSave = requestedRefs.filter(
        (ref) =>
          dirtySharedRefsRef.current.has(ref) &&
          !savingSharedRefsRef.current.has(ref),
      );

      if (refsToSave.length === 0) {
        return;
      }

      const snapshot = refsToSave.reduce<Record<string, unknown>>(
        (result, ref) => {
          result[ref] = answersRef.current[ref];
          savingSharedRefsRef.current.add(ref);
          return result;
        },
        {},
      );

      try {
        const state = await updateSharedFormState(
          form.projectId,
          form.formId,
          snapshot,
          options,
        );
        setResponseId(state.responseId);
        setSubmissionError((current) =>
          current === "Shared form changes could not be saved." ? null : current,
        );

        for (const ref of refsToSave) {
          if (Object.is(answersRef.current[ref], snapshot[ref])) {
            dirtySharedRefsRef.current.delete(ref);
          }
        }
      } catch {
        setSubmissionError("Shared form changes could not be saved.");
      } finally {
        for (const ref of refsToSave) {
          savingSharedRefsRef.current.delete(ref);
        }
      }
    },
    [form, isSharedMode],
  );

  useEffect(() => {
    if (!isSharedMode) {
      return;
    }

    const flushRemaining = () => {
      void flushSharedAnswers(undefined, { keepalive: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushRemaining();
      }
    };

    window.addEventListener("pagehide", flushRemaining);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", flushRemaining);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flushSharedAnswers, isSharedMode]);

  useEffect(() => {
    if (!isSharedMode || !form) {
      return;
    }

    const interval = window.setInterval(() => {
      if (
        dirtySharedRefsRef.current.size > 0 ||
        savingSharedRefsRef.current.size > 0
      ) {
        return;
      }

      void getSharedFormState(form.projectId, form.formId)
        .then((state) => {
          if (
            dirtySharedRefsRef.current.size > 0 ||
            savingSharedRefsRef.current.size > 0
          ) {
            return;
          }

          answersRef.current = state.answers;
          setAnswers(state.answers);
          setResponseId(state.responseId);
        })
        .catch(() => {
          // Polling is a convenience; local editing should continue.
        });
    }, 8_000);

    return () => window.clearInterval(interval);
  }, [form, isSharedMode]);

  const onAnswerChange = useCallback((questionRef: string, value: unknown) => {
    answersRef.current = {
      ...answersRef.current,
      [questionRef]: value,
    };
    if (isSharedMode) {
      dirtySharedRefsRef.current.add(questionRef);
    }
    setAnswers(answersRef.current);
    setSubmissionError((prev) => {
      if (!prev) {
        return prev;
      }
      if (
        prev.startsWith("Please correct") ||
        prev.startsWith("Some answers need your attention")
      ) {
        return null;
      }
      return prev;
    });
    setFieldErrors((prev) => {
      if (!prev.has(questionRef)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(questionRef);
      return next;
    });
    setValidationErrors((prev) =>
      prev.filter((error) => error.questionRef !== questionRef),
    );
  }, [isSharedMode]);

  const onAnswerBlur = useCallback(
    (questionRef: string) => {
      setTouchedQuestions((prev) => {
        if (prev.has(questionRef)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(questionRef);
        return next;
      });

      const question = questions.find((item) => item.ref === questionRef);
      if (!question) {
        return;
      }

      const result = validateAnswersClientSide([question], answersRef.current);

      setFieldErrors((prev) => {
        const next = new Map(prev);
        if (result.errors.length > 0) {
          next.set(
            questionRef,
            result.errors[0]?.message ?? "This answer is invalid",
          );
        } else {
          next.delete(questionRef);
        }
        return next;
      });

      void flushSharedAnswers([questionRef]);
    },
    [flushSharedAnswers, questions],
  );

  const onBack = useCallback(() => {
    handleBack({
      hasPages,
      hasIntro,
      pageIds,
      getQuestionsForPage,
      questions,
      answers,
      visibilityEvaluator,
      setSubmissionError,
      setPageHistory,
      setCurrentPageIndex,
      setCurrentStep,
      setQuestionHistory,
    });
  }, [
    answers,
    getQuestionsForPage,
    hasIntro,
    hasPages,
    pageIds,
    questions,
    visibilityEvaluator,
  ]);

  const getQuestionError = useCallback(
    (questionRef: string): string | undefined => {
      const fieldError = fieldErrors.get(questionRef);
      if (fieldError && touchedQuestions.has(questionRef)) {
        return fieldError;
      }
      return validationErrors.find((error) => error.questionRef === questionRef)
        ?.message;
    },
    [fieldErrors, touchedQuestions, validationErrors],
  );

  const submitWithSession = useCallback(
    async (input: {
      projectId: string;
      formId: string;
      versionNumber: number;
      answers: Record<string, unknown>;
      startedAt: Date;
      skippedQuestionRefs: string[];
      questionPath: string[];
    }) => {
      let activeSession = responseSession;

      const submitLegacy = async () =>
        submitResponse({
          projectId: input.projectId,
          formId: input.formId,
          versionNumber: input.versionNumber,
          answers: input.answers,
          startedAt: input.startedAt,
          skippedQuestionRefs: input.skippedQuestionRefs,
          questionPath: input.questionPath,
        });

      if (!activeSession) {
        try {
          if (!sessionCreationPromiseRef.current) {
            isCreatingSessionRef.current = true;
            sessionCreationPromiseRef.current = createAndStoreSession({
              startedAt: input.startedAt,
              answers: input.answers,
              skippedQuestionRefs: input.skippedQuestionRefs,
              questionPath: input.questionPath,
            }).finally(() => {
              isCreatingSessionRef.current = false;
              sessionCreationPromiseRef.current = null;
            });
          }

          activeSession = await sessionCreationPromiseRef.current;
        } catch {
          const fallbackResult = await submitLegacy();
          lastProgressPayloadRef.current = null;
          return fallbackResult;
        }
      }

      const result = await completeResponseSession(
        activeSession.responseId,
        activeSession.sessionToken,
        {
          answers: input.answers,
          skippedQuestionRefs: input.skippedQuestionRefs,
          questionPath: input.questionPath,
          progressPercentage: 100,
          lastQuestionRef:
            input.questionPath[input.questionPath.length - 1] ??
            effectiveLastQuestionRef,
          lastPageId: hasPages ? pageIds[currentPageIndex] : undefined,
        },
      );

      lastProgressPayloadRef.current = null;
      return result;
    },
    [
      createAndStoreSession,
      currentPageIndex,
      effectiveLastQuestionRef,
      hasPages,
      pageIds,
      responseSession,
    ],
  );

  const onSubmit = useCallback(async () => {
    if (!form) {
      return;
    }
    // Block submission in preview mode
    if (isPreview) {
      setSubmissionError("Submission is disabled in preview mode");
      return;
    }
    if (isSharedMode) {
      await flushSharedAnswers();
      return;
    }
    await handleSubmit({
      form,
      questions,
      answers,
      skippedQuestionRefs,
      questionPath,
      startTime,
      hasPages,
      totalPages,
      totalQuestions,
      projectId,
      formId,
      visibilityEvaluator,
      submitResponse: submitWithSession,
      trackFormEvent,
      clearDraft,
      setSubmissionError,
      setValidationErrors,
      setTouchedQuestions,
      setFieldErrors,
      setIsSubmitting,
      setIsSubmitted,
      setResponseId,
      setCurrentStep,
      setCurrentPageIndex,
    });
  }, [
    answers,
    clearDraft,
    form,
    formId,
    hasPages,
    isPreview,
    isSharedMode,
    projectId,
    questionPath,
    questions,
    skippedQuestionRefs,
    startTime,
    submitWithSession,
    flushSharedAnswers,
    totalPages,
    totalQuestions,
    visibilityEvaluator,
  ]);

  const hasDraftProgress = useMemo(
    () =>
      getHasDraftProgress({
        answers,
        hasIntro,
        isOnIntro,
        hasPages,
        currentPageIndex,
        currentStep,
        questionHistoryLength: questionHistory.length,
        pageHistoryLength: pageHistory.length,
      }),
    [
      answers,
      currentPageIndex,
      currentStep,
      hasIntro,
      hasPages,
      isOnIntro,
      pageHistory.length,
      questionHistory.length,
    ],
  );

  useEffect(() => {
    if (!form || !formIdentity || !shouldUseLocalDrafts) {
      return;
    }
    if (pendingDraft) {
      return;
    }
    if (isSubmitted) {
      return;
    }
    if (!hasDraftProgress) {
      return;
    }

    const nowIso = new Date().toISOString();
    const createdAt = storedDraft?.createdAt ?? nowIso;
    const draft = buildDraftSnapshot({
      form,
      createdAt,
      nowIso,
      startedAtIso: startTime.toISOString(),
      answers,
      currentStep,
      currentPageIndex,
      questionHistory,
      pageHistory,
      questionPath,
    });

    const handle = window.setTimeout(() => {
      saveDraft(draft);
    }, 200);

    return () => window.clearTimeout(handle);
  }, [
    answers,
    currentPageIndex,
    currentStep,
    form,
    formIdentity,
    hasDraftProgress,
    isSubmitted,
    pageHistory,
    pendingDraft,
    questionHistory,
    questionPath,
    saveDraft,
    shouldUseLocalDrafts,
    startTime,
    storedDraft?.createdAt,
  ]);

  const onNext = useCallback(async () => {
    await handleNext({
      isSubmitting,
      isSharedMode,
      hasIntro,
      currentStep,
      hasPages,
      pages: orderedPages,
      currentPageIndex,
      totalPages,
      totalQuestions,
      pageIds,
      currentPageQuestions,
      questions,
      answers,
      skippedQuestionRefs,
      jumpEvaluator,
      visibilityEvaluator,
      getQuestionsForPage,
      onSubmit,
      setCurrentStep,
      setCurrentPageIndex,
      setPageHistory,
      setQuestionHistory,
      setTouchedQuestions,
      setFieldErrors,
      setValidationErrors,
      setSubmissionError,
    });
  }, [
    answers,
    currentPageIndex,
    currentPageQuestions,
    currentStep,
    getQuestionsForPage,
    hasIntro,
    hasPages,
    isSubmitting,
    isSharedMode,
    jumpEvaluator,
    onSubmit,
    orderedPages,
    pageIds,
    questions,
    skippedQuestionRefs,
    totalPages,
    totalQuestions,
    visibilityEvaluator,
  ]);

  const canProceed = useMemo(() => {
    if (isOnIntro) {
      return true;
    }

    if (isSharedMode) {
      return true;
    }

    if (hasPages) {
      const pageQuestions = currentPageQuestions ?? [];
      return pageQuestions.every((question) => {
        if (!shouldRequireAnswer(question)) return true;
        return isAnswerProvided(question, answers[question.ref]);
      });
    }

    if (!currentQuestion) {
      return false;
    }

    if (!shouldRequireAnswer(currentQuestion)) {
      return true;
    }

    const answer = answers[currentQuestion.ref];
    return isAnswerProvided(currentQuestion, answer);
  }, [
    answers,
    currentPageQuestions,
    currentQuestion,
    hasPages,
    isOnIntro,
    isSharedMode,
  ]);

  useKeyboardShortcuts({
    canProceed,
    isOnIntro,
    isComplete,
    onNext,
    onBack,
  });

  const onDismissSubmissionError = useCallback(
    () => setSubmissionError(null),
    [],
  );

  const backDisabled = hasPages
    ? isSubmitting ||
      (pageHistory.length === 0 && !hasIntro && currentPageIndex === 0)
    : isSubmitting ||
      (questionHistory.length === 0 && !hasIntro && currentStep === 0);
  const isLastStep = useMemo(() => {
    if (isOnIntro) {
      return false;
    }

    if (hasPages) {
      for (
        let pageIndex = currentPageIndex + 1;
        pageIndex < totalPages;
        pageIndex += 1
      ) {
        const pageQuestions = getQuestionsForPage(pageIds[pageIndex]);
        const visibleQuestions = visibilityEvaluator.filterVisible(
          pageQuestions,
          answers,
        );
        if (visibleQuestions.length > 0) {
          return false;
        }
      }
      return true;
    }

    if (currentStep < 0 || currentStep >= totalQuestions) {
      return false;
    }

    for (let index = currentStep + 1; index < totalQuestions; index += 1) {
      const question = questions[index];
      if (question && visibilityEvaluator.isVisible(question, answers)) {
        return false;
      }
    }

    return true;
  }, [
    answers,
    currentPageIndex,
    currentStep,
    getQuestionsForPage,
    hasPages,
    isOnIntro,
    pageIds,
    questions,
    totalPages,
    totalQuestions,
    visibilityEvaluator,
  ]);

  return {
    questions,
    totalQuestions,
    currentStep,
    currentQuestion,
    answers,
    submissionError,
    validationErrors,
    isSubmitting,
    isSubmitted,
    responseId,
    uploadSessionToken,
    ensureUploadSessionToken,

    isRestoringDraft: isRestoringDraft || isRestoringSharedState,
    hasDraft: Boolean(activePendingDraft),
    draftUpdatedAt: activePendingDraft?.updatedAt ?? null,
    onResumeDraft,
    onDiscardDraft,

    hasIntro,
    isOnIntro,
    hasPages,
    currentPageIndex,
    currentPageQuestions,
    totalPages,
    isComplete,

    showProgress,
    progressMode,
    progressPercentage,

    canProceed,
    backDisabled,
    isLastStep,

    onAnswerChange,
    onAnswerBlur,
    getQuestionError,
    onBack,
    onNext,
    onDismissSubmissionError,
    onReset: () => {
      clearDraft();
      setPendingDraft(null);
      reset({ preferIntro: hasIntro });
    },
  };
}
